import React from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { formatTaskDate } from "../../pages/Tasks/taskUtils";
import { getContactNameDisplay, getContactPhoneDisplay } from "../../utils/contactPhoneDisplay";
import TaskEditForm from "./TaskEditForm";

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(1.5),
    overflowWrap: "break-word",
    wordBreak: "break-word"
  },
  label: {
    fontWeight: 600
  }
}));

// Cada campo de data usa formatTaskDate, que nunca retorna "Invalid date" —
// ausência/valor inválido caem no texto traduzido de "sem data".
const DateField = ({ classes, labelKey, value, format }) => (
  <Grid item xs={12} sm={6} className={classes.field}>
    <Typography variant="caption" color="textSecondary" className={classes.label}>
      {i18n.t(labelKey)}
    </Typography>
    <Typography variant="body2">{formatTaskDate(value, format) || i18n.t("tasks.details.noDate")}</Typography>
  </Grid>
);

const TaskDetailsTab = ({ task, isEditing, onEditSubmit, onEditCancel }) => {
  const classes = useStyles();

  if (isEditing) {
    return <TaskEditForm task={task} onSubmit={onEditSubmit} onCancel={onEditCancel} />;
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} className={classes.field}>
        <Typography variant="caption" color="textSecondary" className={classes.label}>
          {i18n.t("tasks.details.description")}
        </Typography>
        <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
          {task.description || i18n.t("tasks.details.noDescription")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} className={classes.field}>
        <Typography variant="caption" color="textSecondary" className={classes.label}>
          {i18n.t("tasks.details.assignedUser")}
        </Typography>
        <Typography variant="body2">
          {task.assignedUser?.name || i18n.t("tasks.table.none")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} className={classes.field}>
        <Typography variant="caption" color="textSecondary" className={classes.label}>
          {i18n.t("tasks.details.createdByUser")}
        </Typography>
        <Typography variant="body2">
          {task.createdByUser?.name || i18n.t("tasks.table.none")}
        </Typography>
      </Grid>

      {task.contact && (
        <Grid item xs={12} sm={6} className={classes.field}>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            {i18n.t("tasks.details.contact")}
          </Typography>
          <Typography variant="body2">
            {getContactNameDisplay(task.contact)} —{" "}
            {getContactPhoneDisplay(task.contact, task.contact.number)}
          </Typography>
        </Grid>
      )}

      {task.ticket && (
        <Grid item xs={12} sm={6} className={classes.field}>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            {i18n.t("tasks.details.ticket")}
          </Typography>
          <Typography variant="body2">
            #{task.ticket.uuid ? task.ticket.uuid.slice(0, 8) : task.ticket.id}
          </Typography>
        </Grid>
      )}

      <DateField classes={classes} labelKey="tasks.details.createdAt" value={task.createdAt} format="DD/MM/YYYY HH:mm" />
      <DateField classes={classes} labelKey="tasks.details.dueDate" value={task.dueDate} />
      <DateField classes={classes} labelKey="tasks.details.startedAt" value={task.startedAt} format="DD/MM/YYYY HH:mm" />
      <DateField classes={classes} labelKey="tasks.details.completedAt" value={task.completedAt} format="DD/MM/YYYY HH:mm" />

      {task.status === "done" && (
        <Grid item xs={12} className={classes.field}>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            {i18n.t("tasks.details.completionDescription")}
          </Typography>
          <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
            {task.completionDescription || i18n.t("tasks.details.noCompletionDescription")}
          </Typography>
        </Grid>
      )}

      <DateField classes={classes} labelKey="tasks.details.canceledAt" value={task.canceledAt} format="DD/MM/YYYY HH:mm" />

      {task.contactNotifiedAt && (
        <Grid item xs={12} sm={6} className={classes.field}>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            {i18n.t("tasks.details.contactNotified")}
          </Typography>
          <Typography variant="body2">
            {i18n.t("tasks.details.contactNotifiedStatus")} —{" "}
            {formatTaskDate(task.contactNotifiedAt, "DD/MM/YYYY HH:mm") || i18n.t("tasks.details.noDate")}
            {task.contactNotifiedByUser?.name ? ` — ${task.contactNotifiedByUser.name}` : ""}
          </Typography>
        </Grid>
      )}

      {task.status === "canceled" && (
        <Grid item xs={12} className={classes.field}>
          <Typography variant="caption" color="textSecondary" className={classes.label}>
            {i18n.t("tasks.details.cancelReason")}
          </Typography>
          <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
            {task.cancelReason || i18n.t("tasks.table.none")}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default TaskDetailsTab;
